import {
  type ClaimsCapabilities,
  CoiSpeed,
  IndicationStatus,
  InsuranceType,
  ItemizedCheckoutStyle,
  PricingKind,
  type QuoteCardData,
  QuoteCardStage,
} from '@/components/quotes/quoteCardTypes'
import { DocumentType } from '@/models/document'
import { CoverageType } from '@/models/insuranceCoverage'
import type { InsuranceIndication } from '@/models/insuranceIndication'
import { InsuranceIndicationStatus, Vendor } from '@/models/insuranceTypes'

const CARRIER_LOGO_BASE = 'https://static.cai.insure/carriers'
// Dellwood is no vendor of ours; production serves the logo uploaded onto its carrier record.
const DELLWOOD_ICON_URL =
  'https://static.cai.insure/carrier_logos/03d57f2c-ae7a-4575-bd93-db8f3570b91c/dellwood-icon.png'

// Relative: a pinned date drifts into "-20 days until this quote expires" and re-renders the card daily.
const daysAgo = (days: number) => new Date(Date.now() - days * 86_400_000).toISOString()

// Sourced from carrier-claims-capabilities.json (compiled 2026-07-29) — facts only, no Cai-authored rating.
const CLAIMS_CAPABILITIES: Record<string, ClaimsCapabilities> = {
  geico: {
    hasMultipleWaysToFile: true,
    has247Support: true,
    hasPayoutInformation: true,
    waysToFile: 'File online, through the GEICO mobile app, or by phone',
    supportAvailability: 'Available anytime',
    payoutSpeed: 'As little as 48 hours',
    waysToGetPaid: 'Digital payment or mail',
  },
  progressive: {
    hasMultipleWaysToFile: true,
    has247Support: true,
    hasPayoutInformation: false,
    waysToFile: 'File online or by phone at 1-800-776-4737',
    supportAvailability: '24/7 phone support',
    payoutSpeed: 'No information',
    waysToGetPaid: 'No information',
  },
  nirvana: {
    hasMultipleWaysToFile: true,
    has247Support: true,
    hasPayoutInformation: false,
    waysToFile: 'File online through the Nirvana Safety Intelligence Platform, by email, or by phone',
    supportAvailability: '24/7 reporting',
    payoutSpeed: 'No information',
    waysToGetPaid: 'No information',
  },
  corgi: {
    hasMultipleWaysToFile: true,
    has247Support: false,
    hasPayoutInformation: false,
    waysToFile: 'File by phone or through the online claims portal',
    supportAvailability: 'No information',
    payoutSpeed: 'No information',
    waysToGetPaid: 'No information',
  },
  canal: {
    hasMultipleWaysToFile: true,
    has247Support: true,
    hasPayoutInformation: false,
    waysToFile: 'File by phone, online form, or the Canal mobile app',
    supportAvailability: '24/7 phone support',
    payoutSpeed: 'No information',
    waysToGetPaid: 'No information',
  },
  coverWhale: {
    hasMultipleWaysToFile: false,
    has247Support: false,
    hasPayoutInformation: false,
    waysToFile: 'File online',
    supportAvailability: 'No information',
    payoutSpeed: 'No information',
    waysToGetPaid: 'No information',
  },
  hanover: {
    hasMultipleWaysToFile: true,
    has247Support: true,
    hasPayoutInformation: true,
    waysToFile: 'File online (no login required) or by phone',
    supportAvailability: '24/7 phone support',
    payoutSpeed: 'Digital payment typically within 24 hours; paper check typically 5-7 days',
    waysToGetPaid: "Digital payment or paper check, policyholder's choice",
  },
}

/** Internal state/stage label shown next to each card so reviewers can reference a specific one in feedback. */
export function describeQuote(quote: QuoteCardData): string {
  const shared = []
  if (quote.memberIds && quote.memberIds.length > 1) shared.push('GROUPED')
  if (quote.coverageOutcomes?.length) shared.push('COVERAGE_OUTCOMES')
  if (quote.outstandingDocuments?.length) shared.push('OUTSTANDING_DOCUMENTS')
  if (quote.failureReason) shared.push('FAILURE_REASON')
  if (quote.sourcedThrough) shared.push('SOURCED_THROUGH')
  if (quote.stage !== QuoteCardStage.PRICED) {
    if (quote.needsAttestation) shared.push('NEEDS_ATTESTATION')
    if (quote.statusMessage) shared.push('STATUS_MESSAGE')
    return [quote.stage.toUpperCase(), ...shared].join(' · ')
  }
  const parts = ['PRICED', quote.pricingKind.toUpperCase(), ...shared]
  if (quote.pricingKind === PricingKind.QUOTED && !quote.itemizedCheckoutStyle) parts.push('BUNDLED')
  if (quote.indicationStatus) parts.push(quote.indicationStatus.toUpperCase())
  if (quote.isEstimateOnly) parts.push('ESTIMATE_ONLY')
  if (quote.totalPremiumRange) parts.push('PREMIUM_RANGE')
  if (quote.failedStage) parts.push(`FAILED_${quote.failedStage.toUpperCase()}`)
  if (quote.supersededAt) parts.push('SUPERSEDED')
  if (quote.coverages.some((coverage) => coverage.premium === null)) parts.push('TOTAL_ONLY')
  if (quote.coverages.some((coverage) => coverage.comprehensiveDeductible != null)) {
    parts.push('COMPREHENSIVE_DEDUCTIBLE')
  }
  if (quote.coverages.some((coverage) => coverage.physicalDamageUnitCount && coverage.limit)) {
    parts.push('PD_UNIT_COUNT')
  }
  if (
    quote.coverages.some((coverage) => coverage.type === CoverageType.AUTO_PHYSICAL_DAMAGE && !coverage.limit)
  ) {
    parts.push('STATED_VALUE')
  }
  if (quote.coverages.some((coverage) => coverage.aggregateLimit != null)) parts.push('AGGREGATE_LIMIT')
  if (quote.coverages.some((coverage) => coverage.restrictionNote)) parts.push('RESTRICTION_NOTE')
  if (quote.financing && !quote.financing.isStandard) parts.push('FINANCING_ON_REQUEST')
  if (quote.financing?.plans?.length) parts.push('PAYMENT_PLANS')
  if (quote.moratorium) parts.push('MORATORIUM')
  if (quote.isCustomerUploaded) parts.push('CUSTOMER_UPLOADED')
  if (quote.appliedAt) parts.push('APPLIED')
  if (quote.itemizedCheckoutStyle) parts.push(quote.itemizedCheckoutStyle.toUpperCase())
  return parts.join(' · ')
}

const STANDARD_FINANCING = {
  isStandard: true,
  description: 'Split your premium into 10 monthly payments at 0% APR.',
}

/** Every card here is a shape applicationToQuoteCards can produce from a real payload. */
export const QUOTES: QuoteCardData[] = [
  {
    id: 'biberk-preparing',
    carrierName: 'Biberk',
    iconUrl: `${CARRIER_LOGO_BASE}/biberk-icon.png`,
    iconAlt: 'Biberk',
    carrierRating: 'A-',
    coiSpeed: CoiSpeed.ONE_DAY_OR_MORE,
    stage: QuoteCardStage.PREPARING,
  },
  {
    // Corgi asks for a loss run seconds after dispatch and then stops working,
    // so this state is a request for a file, not progress.
    id: 'corgi-needs-loss-run',
    carrierName: 'Corgi',
    iconUrl: `${CARRIER_LOGO_BASE}/corgi-icon.png`,
    iconAlt: 'Corgi',
    carrierRating: 'A-',
    coiSpeed: CoiSpeed.UNDER_24_HOURS,
    stage: QuoteCardStage.PREPARING,
    outstandingDocuments: [DocumentType.LOSS_RUN, DocumentType.IFTA],
  },
  {
    id: 'canal-application-sent',
    carrierName: 'Canal',
    iconUrl: `${CARRIER_LOGO_BASE}/canal-icon.png`,
    iconAlt: 'Canal',
    carrierRating: 'B+',
    coiSpeed: CoiSpeed.UNDER_24_HOURS,
    stage: QuoteCardStage.APPLICATION_SENT,
  },
  {
    id: 'hanover-needs-attestation-minimal',
    carrierName: 'Hanover',
    iconUrl: `${CARRIER_LOGO_BASE}/hanover-icon.png`,
    iconAlt: 'Hanover',
    carrierRating: 'B',
    coiSpeed: CoiSpeed.ONE_DAY_OR_MORE,
    stage: QuoteCardStage.APPLICATION_SENT,
    needsAttestation: true,
  },
  {
    id: 'nirvana',
    carrierName: 'Nirvana Insurance',
    insuranceType: InsuranceType.NON_ADMITTED,
    iconUrl: `${CARRIER_LOGO_BASE}/nirvana-icon.png`,
    iconAlt: 'Nirvana',
    carrierRating: 'A',
    coiSpeed: CoiSpeed.ONE_DAY_OR_MORE,
    carrierPublicNote: 'ELD / dashcam: Required pre-bind — confirm before start.',
    // Already chosen at checkout, so it carries no underwriting status: applied means the carrier had what it asked for.
    appliedAt: '2026-08-02T09:12:00',
    stage: QuoteCardStage.PRICED,
    pricingKind: PricingKind.INDICATION,
    coverages: [
      {
        type: CoverageType.AUTO_LIABILITY,
        premium: 13800,
        limit: 1_000_000,
        deductible: 0,
      },
    ],
    financing: STANDARD_FINANCING,
    claimsCapabilities: CLAIMS_CAPABILITIES.nirvana,
  },
  {
    // Quote-backed on purpose: a superseded estimate is also estimate-only, which blocks checkout.
    id: 'nirvana-superseded',
    carrierName: 'Nirvana Insurance',
    insuranceType: InsuranceType.NON_ADMITTED,
    iconUrl: `${CARRIER_LOGO_BASE}/nirvana-icon.png`,
    iconAlt: 'Nirvana',
    carrierRating: 'A',
    coiSpeed: CoiSpeed.ONE_DAY_OR_MORE,
    supersededAt: daysAgo(20),
    stage: QuoteCardStage.PRICED,
    pricingKind: PricingKind.QUOTED,
    quotedAt: daysAgo(20),
    coverages: [
      {
        type: CoverageType.AUTO_LIABILITY,
        premium: 14900,
        limit: 1_000_000,
        deductible: 0,
      },
    ],
    financing: STANDARD_FINANCING,
    claimsCapabilities: CLAIMS_CAPABILITIES.nirvana,
  },
  {
    // Dellwood's carrier records carry no financing text, so a firm Dellwood quote has none.
    id: 'dellwood-quoted-no-financing',
    carrierName: 'Dellwood',
    insuranceType: InsuranceType.NON_ADMITTED,
    iconUrl: DELLWOOD_ICON_URL,
    iconAlt: 'Dellwood',
    carrierRating: 'A-',
    coiSpeed: CoiSpeed.UNDER_24_HOURS,
    stage: QuoteCardStage.PRICED,
    pricingKind: PricingKind.QUOTED,
    quotedAt: daysAgo(3),
    coverages: [
      {
        type: CoverageType.MOTOR_TRUCK_CARGO,
        premium: 1229.51,
        limit: 100_000,
        deductible: 1_000,
      },
      {
        type: CoverageType.AUTO_PHYSICAL_DAMAGE,
        premium: 2314.47,
        limit: 30_000,
        deductible: 2_500,
      },
    ],
  },
  {
    id: 'geico',
    carrierName: 'GEICO',
    iconUrl: `${CARRIER_LOGO_BASE}/geico-icon.png`,
    iconAlt: 'GEICO',
    carrierRating: 'A+',
    coiSpeed: CoiSpeed.UNDER_24_HOURS,
    carrierPublicNote: 'Offers accident forgiveness after 3 years claims-free.',
    brokerRecommended: true,
    recommendedByName: 'Debbie Benning',
    brokerNote: {
      note: 'Recommending GEICO for this renewal. You mentioned your last claim with your current carrier took over two months to close, and GEICO has been consistently faster for our other fleet clients. Price is competitive with the coverage you need.',
    },
    stage: QuoteCardStage.PRICED,
    pricingKind: PricingKind.QUOTED,
    quotedAt: daysAgo(10),
    coverages: [
      {
        type: CoverageType.AUTO_LIABILITY,
        premium: 12400,
        limit: 1_000_000,
        deductible: 0,
      },
      {
        type: CoverageType.GENERAL_LIABILITY,
        premium: 2100,
        limit: 1_000_000,
        deductible: 500,
      },
    ],
    financing: STANDARD_FINANCING,
    claimsCapabilities: CLAIMS_CAPABILITIES.geico,
    quoteDocuments: [{ url: 'https://example.com/quotes/geico-quote.pdf', addedAt: daysAgo(10) }],
  },
  {
    // GEICO's 12-month plans for MBM Trucking (qa_fixtures/geico_supergood), scaled to the median GEICO price (~$25k).
    id: 'geico-payment-plans',
    carrierName: 'GEICO',
    iconUrl: `${CARRIER_LOGO_BASE}/geico-icon.png`,
    iconAlt: 'GEICO',
    carrierRating: 'A+',
    coiSpeed: CoiSpeed.UNDER_24_HOURS,
    stage: QuoteCardStage.PRICED,
    // Plans come with GEICO's firm quote, never with an estimate.
    pricingKind: PricingKind.QUOTED,
    coverages: [
      {
        type: CoverageType.AUTO_LIABILITY,
        premium: 20_876,
        limit: 1_000_000,
        deductible: 0,
      },
      {
        type: CoverageType.GENERAL_LIABILITY,
        premium: 4124,
        limit: 1_000_000,
        deductible: 500,
      },
    ],
    financing: {
      isStandard: true,
      plansNote: 'Plans with more than one payment require auto pay.',
      // GEICO quotes installments to the cent and totals to the dollar, so a row is off by a few cents.
      plans: [
        { title: 'Pay in full', dueToday: 22_553, installments: null, total: 22_553 },
        {
          title: '4 payments',
          dueToday: 6317.26,
          installments: { count: 3, amount: 6248.53 },
          total: 25_063,
        },
        {
          title: '9 payments',
          dueToday: 4472.35,
          installments: { count: 8, amount: 2583.64 },
          total: 25_141,
        },
        {
          title: '11 payments',
          dueToday: 4240.49,
          installments: { count: 10, amount: 2093.22 },
          total: 25_173,
        },
      ],
    },
    claimsCapabilities: CLAIMS_CAPABILITIES.geico,
  },
  {
    id: 'corgi-itemized',
    carrierName: 'Corgi',
    iconUrl: `${CARRIER_LOGO_BASE}/corgi-icon.png`,
    iconAlt: 'Corgi',
    carrierRating: 'A-',
    coiSpeed: CoiSpeed.UNDER_24_HOURS,
    stage: QuoteCardStage.PRICED,
    pricingKind: PricingKind.QUOTED,
    quotedAt: daysAgo(8),
    itemizedCheckoutStyle: ItemizedCheckoutStyle.CAN_PURCHASE_INDIVIDUAL,
    memberIds: ['corgi-itemized', 'corgi-itemized-cargo'],
    // Each line its own indication, which is what makes a coverage checkoutable on its own.
    coverages: [
      {
        type: CoverageType.AUTO_LIABILITY,
        premium: 11800,
        limit: 1_000_000,
        deductible: 0,
        indicationId: 'corgi-itemized',
      },
      {
        type: CoverageType.MOTOR_TRUCK_CARGO,
        premium: 1600,
        limit: 100_000,
        deductible: 1000,
        indicationId: 'corgi-itemized-cargo',
      },
    ],
    financing: STANDARD_FINANCING,
    claimsCapabilities: CLAIMS_CAPABILITIES.corgi,
  },
  {
    // Corgi v2 prices the policy as one total, so each coverage row carries no premium.
    id: 'corgi-total-only',
    carrierName: 'Corgi',
    iconUrl: `${CARRIER_LOGO_BASE}/corgi-icon.png`,
    iconAlt: 'Corgi',
    carrierRating: 'A-',
    coiSpeed: CoiSpeed.UNDER_24_HOURS,
    stage: QuoteCardStage.PRICED,
    pricingKind: PricingKind.INDICATION,
    totalPremium: 13_250,
    coverages: [
      {
        type: CoverageType.AUTO_LIABILITY,
        premium: null,
        limit: 1_000_000,
        deductible: 0,
      },
      {
        type: CoverageType.MOTOR_TRUCK_CARGO,
        premium: null,
        limit: 100_000,
        deductible: 1000,
      },
    ],
    financing: STANDARD_FINANCING,
    claimsCapabilities: CLAIMS_CAPABILITIES.corgi,
  },
  {
    id: 'canal',
    carrierName: 'Canal',
    insuranceType: InsuranceType.ADMITTED,
    iconUrl: `${CARRIER_LOGO_BASE}/canal-icon.png`,
    iconAlt: 'Canal',
    carrierRating: 'B+',
    coiSpeed: CoiSpeed.UNDER_24_HOURS,
    isCurrentInsurer: true,
    stage: QuoteCardStage.PRICED,
    pricingKind: PricingKind.INDICATION,
    coverages: [
      {
        type: CoverageType.AUTO_LIABILITY,
        premium: 14600,
        limit: 1_000_000,
        deductible: 0,
      },
    ],
    financing: STANDARD_FINANCING,
    claimsCapabilities: CLAIMS_CAPABILITIES.canal,
  },
  {
    // The attribution line's one production trigger: Rocklake's Progressive beside a direct Progressive.
    id: 'progressive-via-rocklake',
    carrierName: 'Progressive',
    iconUrl: `${CARRIER_LOGO_BASE}/progressive-icon.png`,
    iconAlt: 'Progressive',
    carrierRating: 'A',
    coiSpeed: CoiSpeed.UNDER_24_HOURS,
    insuranceType: InsuranceType.ADMITTED,
    sourcedThrough: 'Rocklake',
    stage: QuoteCardStage.PRICED,
    pricingKind: PricingKind.INDICATION,
    coverages: [
      {
        type: CoverageType.AUTO_LIABILITY,
        premium: 13300,
        limit: 1_000_000,
        deductible: 0,
      },
    ],
    financing: STANDARD_FINANCING,
    claimsCapabilities: CLAIMS_CAPABILITIES.progressive,
  },
  {
    // Rocklake routinely prices several Palomar papers on one application; the program
    // is the only thing telling those cards apart. Palomar's carrier record has no
    // rating, COI speed, financing or claims data, so every carrier-details row blanks.
    id: 'palomar-program',
    carrierName: 'Palomar',
    carrierProgram: 'PESIC',
    iconUrl: `${CARRIER_LOGO_BASE}/generic.png`,
    iconAlt: 'Palomar',
    stage: QuoteCardStage.PRICED,
    pricingKind: PricingKind.INDICATION,
    coverages: [
      {
        type: CoverageType.AUTO_LIABILITY,
        premium: 15200,
        limit: 1_000_000,
        deductible: 2500,
      },
    ],
  },
  {
    id: 'coverwhale',
    carrierName: 'Cover Whale',
    iconUrl: `${CARRIER_LOGO_BASE}/coverwhale-icon.png`,
    iconAlt: 'Cover Whale',
    carrierRating: 'A-',
    coiSpeed: CoiSpeed.ONE_DAY_OR_MORE,
    stage: QuoteCardStage.PRICED,
    pricingKind: PricingKind.INDICATION,
    coverages: [
      {
        type: CoverageType.AUTO_PHYSICAL_DAMAGE,
        premium: 4200,
        limit: 150_000,
        deductible: 2500,
        comprehensiveDeductible: 1000,
      },
    ],
    // The surplus-lines carriers add these on top of the premiums; the headline includes them.
    oneTimeFees: [{ id: 'fee-cw-1', label: 'Taxes & fees', amount: 618.45 }],
    financing: STANDARD_FINANCING,
    claimsCapabilities: CLAIMS_CAPABILITIES.coverWhale,
  },
  {
    id: 'coverwhale-pd-unit-count',
    carrierName: 'Cover Whale',
    iconUrl: `${CARRIER_LOGO_BASE}/coverwhale-icon.png`,
    iconAlt: 'Cover Whale',
    carrierRating: 'A-',
    coiSpeed: CoiSpeed.ONE_DAY_OR_MORE,
    stage: QuoteCardStage.PRICED,
    pricingKind: PricingKind.INDICATION,
    coverages: [
      {
        type: CoverageType.AUTO_PHYSICAL_DAMAGE,
        premium: 3100,
        limit: 93_500,
        deductible: 2500,
        comprehensiveDeductible: 1000,
        physicalDamageUnitCount: 10,
      },
    ],
    oneTimeFees: [{ id: 'fee-cw-2', label: 'Taxes & fees', amount: 456.2 }],
    financing: STANDARD_FINANCING,
    claimsCapabilities: CLAIMS_CAPABILITIES.coverWhale,
  },
  {
    // Nirvana stores a $0 limit: the fleet's value is unknown, so the card says Stated Value.
    id: 'nirvana-pd-stated-value',
    carrierName: 'Nirvana Insurance',
    insuranceType: InsuranceType.NON_ADMITTED,
    iconUrl: `${CARRIER_LOGO_BASE}/nirvana-icon.png`,
    iconAlt: 'Nirvana',
    carrierRating: 'A',
    coiSpeed: CoiSpeed.ONE_DAY_OR_MORE,
    stage: QuoteCardStage.PRICED,
    pricingKind: PricingKind.INDICATION,
    coverages: [
      {
        type: CoverageType.AUTO_PHYSICAL_DAMAGE,
        premium: 3650,
        limit: 0,
        deductible: 2500,
      },
    ],
    financing: STANDARD_FINANCING,
    claimsCapabilities: CLAIMS_CAPABILITIES.nirvana,
  },
  {
    // Only Corgi's integrations set the manual-review flag behind this status.
    id: 'corgi-reviewing',
    carrierName: 'Corgi',
    iconUrl: `${CARRIER_LOGO_BASE}/corgi-icon.png`,
    iconAlt: 'Corgi',
    carrierRating: 'A-',
    coiSpeed: CoiSpeed.UNDER_24_HOURS,
    stage: QuoteCardStage.PRICED,
    pricingKind: PricingKind.INDICATION,
    indicationStatus: IndicationStatus.REVIEWING_SUBMITTED_INFO,
    coverages: [
      {
        type: CoverageType.AUTO_LIABILITY,
        premium: 15400,
        limit: 1_000_000,
        deductible: 0,
      },
    ],
    financing: STANDARD_FINANCING,
    claimsCapabilities: CLAIMS_CAPABILITIES.corgi,
  },
  {
    id: 'hanover-attestation',
    carrierName: 'Hanover',
    iconUrl: `${CARRIER_LOGO_BASE}/hanover-icon.png`,
    iconAlt: 'Hanover',
    carrierRating: 'B',
    coiSpeed: CoiSpeed.ONE_DAY_OR_MORE,
    stage: QuoteCardStage.PRICED,
    pricingKind: PricingKind.INDICATION,
    indicationStatus: IndicationStatus.NEEDS_ATTESTATION,
    coverages: [
      {
        type: CoverageType.MOTOR_TRUCK_CARGO,
        premium: 1600,
        limit: 100_000,
        deductible: 1000,
      },
    ],
    financing: STANDARD_FINANCING,
    claimsCapabilities: CLAIMS_CAPABILITIES.hanover,
  },
  {
    // Priced by us without the file the carrier asked for, so the cart waits on the upload.
    id: 'corgi-estimate-needs-documents',
    carrierName: 'Corgi',
    iconUrl: `${CARRIER_LOGO_BASE}/corgi-icon.png`,
    iconAlt: 'Corgi',
    carrierRating: 'A-',
    coiSpeed: CoiSpeed.UNDER_24_HOURS,
    outstandingDocuments: [DocumentType.LOSS_RUN],
    stage: QuoteCardStage.PRICED,
    pricingKind: PricingKind.INDICATION,
    isEstimateOnly: true,
    totalPremiumRange: { low: 10_900, high: 19_400 },
    coverages: [
      {
        type: CoverageType.AUTO_LIABILITY,
        premium: 12100,
        premiumRange: { low: 10_900, high: 19_400 },
        limit: 1_000_000,
        deductible: 0,
      },
    ],
    financing: STANDARD_FINANCING,
    claimsCapabilities: CLAIMS_CAPABILITIES.corgi,
  },
  {
    id: 'geico-estimate-only',
    carrierName: 'GEICO',
    iconUrl: `${CARRIER_LOGO_BASE}/geico-icon.png`,
    iconAlt: 'GEICO',
    carrierRating: 'A+',
    coiSpeed: CoiSpeed.UNDER_24_HOURS,
    stage: QuoteCardStage.PRICED,
    pricingKind: PricingKind.INDICATION,
    isEstimateOnly: true,
    coverages: [
      {
        type: CoverageType.AUTO_LIABILITY,
        premium: 12800,
        limit: 1_000_000,
        deductible: 0,
      },
    ],
    financing: STANDARD_FINANCING,
    claimsCapabilities: CLAIMS_CAPABILITIES.geico,
  },
  {
    // The estimate stays visible beside the failure so this page and the Insurance page agree.
    id: 'geico-estimate-declined',
    carrierName: 'GEICO',
    iconUrl: `${CARRIER_LOGO_BASE}/geico-icon.png`,
    iconAlt: 'GEICO',
    carrierRating: 'A+',
    coiSpeed: CoiSpeed.UNDER_24_HOURS,
    failureReason: "GEICO doesn't insure fleets hauling hazardous materials.",
    stage: QuoteCardStage.PRICED,
    pricingKind: PricingKind.INDICATION,
    isEstimateOnly: true,
    failedStage: QuoteCardStage.DECLINED,
    coverages: [
      {
        type: CoverageType.AUTO_LIABILITY,
        premium: 12800,
        limit: 1_000_000,
        deductible: 0,
      },
    ],
    financing: STANDARD_FINANCING,
    claimsCapabilities: CLAIMS_CAPABILITIES.geico,
  },
  {
    // Direct Progressive reads its liability limit and cargo terms back from the rated quote.
    id: 'progressive',
    carrierName: 'Progressive',
    iconUrl: `${CARRIER_LOGO_BASE}/progressive-icon.png`,
    iconAlt: 'Progressive',
    carrierRating: 'A',
    coiSpeed: CoiSpeed.UNDER_24_HOURS,
    stage: QuoteCardStage.PRICED,
    pricingKind: PricingKind.QUOTED,
    quotedAt: daysAgo(25),
    coverages: [
      {
        type: CoverageType.AUTO_LIABILITY,
        premium: 12900,
        limit: 1_000_000,
        deductible: 0,
      },
      {
        type: CoverageType.MOTOR_TRUCK_CARGO,
        premium: 1800,
        limit: 100_000,
        deductible: 1000,
      },
    ],
    financing: STANDARD_FINANCING,
    claimsCapabilities: CLAIMS_CAPABILITIES.progressive,
  },
  {
    id: 'customer-uploaded',
    carrierName: 'Hanover',
    iconUrl: `${CARRIER_LOGO_BASE}/hanover-icon.png`,
    iconAlt: 'Hanover',
    carrierRating: 'B',
    coiSpeed: CoiSpeed.ONE_DAY_OR_MORE,
    isCustomerUploaded: true,
    uploadedAt: daysAgo(15),
    stage: QuoteCardStage.PRICED,
    pricingKind: PricingKind.QUOTED,
    quotedAt: daysAgo(15),
    coverages: [
      {
        type: CoverageType.AUTO_LIABILITY,
        premium: 13500,
        limit: 1_000_000,
        deductible: 0,
      },
    ],
    financing: STANDARD_FINANCING,
    claimsCapabilities: CLAIMS_CAPABILITIES.hanover,
  },
  {
    id: 'biberk-request-to-bind',
    carrierName: 'Biberk',
    iconUrl: `${CARRIER_LOGO_BASE}/biberk-icon.png`,
    iconAlt: 'Biberk',
    carrierRating: 'A',
    coiSpeed: CoiSpeed.UNDER_24_HOURS,
    stage: QuoteCardStage.REQUEST_TO_BIND,
  },
  {
    id: 'canal-bound',
    carrierName: 'Canal',
    iconUrl: `${CARRIER_LOGO_BASE}/canal-icon.png`,
    iconAlt: 'Canal',
    carrierRating: 'B+',
    coiSpeed: CoiSpeed.ONE_DAY_OR_MORE,
    stage: QuoteCardStage.BOUND,
  },
  {
    id: 'nirvana-declined',
    carrierName: 'Nirvana Insurance',
    iconUrl: `${CARRIER_LOGO_BASE}/nirvana-icon.png`,
    iconAlt: 'Nirvana',
    carrierRating: 'A',
    coiSpeed: CoiSpeed.ONE_DAY_OR_MORE,
    stage: QuoteCardStage.DECLINED,
  },
  {
    id: 'nirvana-declined-explained',
    carrierName: 'Nirvana Insurance',
    iconUrl: `${CARRIER_LOGO_BASE}/nirvana-icon.png`,
    iconAlt: 'Nirvana',
    carrierRating: 'A',
    coiSpeed: CoiSpeed.ONE_DAY_OR_MORE,
    stage: QuoteCardStage.DECLINED,
    failureReason: "Nirvana doesn't write fleets with fewer than 2 years under this DOT number.",
  },
  {
    id: 'progressive-error',
    carrierName: 'Progressive',
    iconUrl: `${CARRIER_LOGO_BASE}/progressive-icon.png`,
    iconAlt: 'Progressive',
    carrierRating: 'B+',
    coiSpeed: CoiSpeed.ONE_DAY_OR_MORE,
    stage: QuoteCardStage.ERROR,
  },
  {
    id: 'hanover-unavailable',
    carrierName: 'Hanover',
    iconUrl: `${CARRIER_LOGO_BASE}/hanover-icon.png`,
    iconAlt: 'Hanover',
    carrierRating: 'A-',
    coiSpeed: CoiSpeed.UNDER_24_HOURS,
    stage: QuoteCardStage.UNAVAILABLE,
  },
]

/**
 * Where `canal-application-sent` lands once the carrier responds. Same carrier and
 * logo on purpose — a card resolves in place, so the transition demo would read as
 * a different card entirely if the identity changed mid-morph. Kept out of QUOTES
 * so the column doesn't gain a duplicate Canal card.
 */
export const APPLICATION_SENT_RESOLVED: QuoteCardData = {
  id: 'canal-resolved',
  carrierName: 'Canal',
  iconUrl: `${CARRIER_LOGO_BASE}/canal-icon.png`,
  iconAlt: 'Canal',
  carrierRating: 'B+',
  coiSpeed: CoiSpeed.UNDER_24_HOURS,
  stage: QuoteCardStage.PRICED,
  pricingKind: PricingKind.INDICATION,
  coverages: [
    {
      type: CoverageType.AUTO_LIABILITY,
      premium: 13800,
      limit: 1_000_000,
      deductible: 0,
    },
  ],
  financing: STANDARD_FINANCING,
  claimsCapabilities: CLAIMS_CAPABILITIES.canal,
}

/** Card treatments QuoteCard renders but the production mapper never produces. */
export const UNIMPLEMENTED_QUOTES: QuoteCardData[] = [
  {
    id: 'geico-aggregate-limit',
    carrierName: 'GEICO',
    iconUrl: `${CARRIER_LOGO_BASE}/geico-icon.png`,
    iconAlt: 'GEICO',
    carrierRating: 'A+',
    coiSpeed: CoiSpeed.UNDER_24_HOURS,
    stage: QuoteCardStage.PRICED,
    pricingKind: PricingKind.INDICATION,
    coverages: [
      {
        type: CoverageType.GENERAL_LIABILITY,
        premium: 2100,
        limit: 1_000_000,
        aggregateLimit: 2_000_000,
        deductible: 500,
      },
    ],
    financing: STANDARD_FINANCING,
    claimsCapabilities: CLAIMS_CAPABILITIES.geico,
  },
  {
    id: 'canal-status-message',
    carrierName: 'Canal',
    iconUrl: `${CARRIER_LOGO_BASE}/canal-icon.png`,
    iconAlt: 'Canal',
    carrierRating: 'B+',
    coiSpeed: CoiSpeed.UNDER_24_HOURS,
    stage: QuoteCardStage.APPLICATION_SENT,
    statusMessage: 'Application sent. Canal typically takes 2 days to respond.',
  },
  {
    id: 'canal-will-request-info',
    carrierName: 'Canal',
    iconUrl: `${CARRIER_LOGO_BASE}/canal-icon.png`,
    iconAlt: 'Canal',
    carrierRating: 'B+',
    coiSpeed: CoiSpeed.UNDER_24_HOURS,
    stage: QuoteCardStage.PRICED,
    pricingKind: PricingKind.INDICATION,
    indicationStatus: IndicationStatus.WILL_REQUEST_INFO,
    coverages: [
      {
        type: CoverageType.AUTO_LIABILITY,
        premium: 13800,
        limit: 1_000_000,
        deductible: 0,
      },
    ],
    financing: STANDARD_FINANCING,
    claimsCapabilities: CLAIMS_CAPABILITIES.canal,
  },
  {
    id: 'canal-awaiting-loss-runs',
    carrierName: 'Canal',
    iconUrl: `${CARRIER_LOGO_BASE}/canal-icon.png`,
    iconAlt: 'Canal',
    carrierRating: 'B+',
    coiSpeed: CoiSpeed.UNDER_24_HOURS,
    stage: QuoteCardStage.PRICED,
    pricingKind: PricingKind.INDICATION,
    indicationStatus: IndicationStatus.AWAITING_LOSS_RUNS,
    coverages: [
      {
        type: CoverageType.AUTO_LIABILITY,
        premium: 14600,
        limit: 1_000_000,
        deductible: 0,
      },
    ],
    financing: STANDARD_FINANCING,
    claimsCapabilities: CLAIMS_CAPABILITIES.canal,
  },
  {
    id: 'coverwhale-has-questions',
    carrierName: 'Cover Whale',
    iconUrl: `${CARRIER_LOGO_BASE}/coverwhale-icon.png`,
    iconAlt: 'Cover Whale',
    carrierRating: 'A-',
    coiSpeed: CoiSpeed.ONE_DAY_OR_MORE,
    stage: QuoteCardStage.PRICED,
    pricingKind: PricingKind.INDICATION,
    indicationStatus: IndicationStatus.HAS_QUESTIONS,
    coverages: [
      {
        type: CoverageType.AUTO_PHYSICAL_DAMAGE,
        premium: 4200,
        limit: 150_000,
        deductible: 2500,
      },
    ],
    financing: STANDARD_FINANCING,
    claimsCapabilities: CLAIMS_CAPABILITIES.coverWhale,
  },
  {
    id: 'progressive-moratorium',
    carrierName: 'Progressive',
    iconUrl: `${CARRIER_LOGO_BASE}/progressive-icon.png`,
    iconAlt: 'Progressive',
    carrierRating: 'A',
    coiSpeed: CoiSpeed.UNDER_24_HOURS,
    stage: QuoteCardStage.PRICED,
    pricingKind: PricingKind.QUOTED,
    quotedAt: daysAgo(25),
    coverages: [
      {
        type: CoverageType.AUTO_LIABILITY,
        premium: 12900,
        limit: 1_000_000,
        deductible: 0,
      },
    ],
    financing: STANDARD_FINANCING,
    claimsCapabilities: CLAIMS_CAPABILITIES.progressive,
    moratorium: {
      message:
        'This carrier is blocking the purchase of coverage in Florida until hurricane Dale passes. Cai will notify you when this moratorium lifts.',
    },
  },
  {
    id: 'progressive-restriction-note',
    carrierName: 'Progressive',
    iconUrl: `${CARRIER_LOGO_BASE}/progressive-icon.png`,
    iconAlt: 'Progressive',
    carrierRating: 'A',
    coiSpeed: CoiSpeed.UNDER_24_HOURS,
    stage: QuoteCardStage.PRICED,
    pricingKind: PricingKind.QUOTED,
    quotedAt: daysAgo(25),
    coverages: [
      {
        type: CoverageType.AUTO_LIABILITY,
        premium: 12900,
        limit: 1_000_000,
        deductible: 0,
      },
      {
        type: CoverageType.MOTOR_TRUCK_CARGO,
        premium: 1800,
        limit: 100_000,
        deductible: 1000,
        restrictionNote: 'Only applies to certain trucks in your fleet.',
      },
    ],
    financing: STANDARD_FINANCING,
    claimsCapabilities: CLAIMS_CAPABILITIES.progressive,
  },
  {
    id: 'hanover-financing-on-request',
    carrierName: 'Hanover',
    iconUrl: `${CARRIER_LOGO_BASE}/hanover-icon.png`,
    iconAlt: 'Hanover',
    carrierRating: 'B',
    coiSpeed: CoiSpeed.ONE_DAY_OR_MORE,
    stage: QuoteCardStage.PRICED,
    pricingKind: PricingKind.INDICATION,
    coverages: [
      {
        type: CoverageType.MOTOR_TRUCK_CARGO,
        premium: 1600,
        limit: 100_000,
        deductible: 1000,
      },
    ],
    financing: {
      isStandard: false,
      description: 'Ask your broker about a custom payment plan for fleets over 10 trucks.',
    },
    claimsCapabilities: CLAIMS_CAPABILITIES.hanover,
  },
  {
    // Production only attributes Rocklake; a hand-placed Amwins offer carries no attribution.
    id: 'palomar-via-amwins',
    carrierName: 'Palomar',
    carrierProgram: 'PESIC',
    iconUrl: `${CARRIER_LOGO_BASE}/generic.png`,
    iconAlt: 'Palomar',
    sourcedThrough: 'Amwins',
    stage: QuoteCardStage.PRICED,
    pricingKind: PricingKind.INDICATION,
    coverages: [
      {
        type: CoverageType.AUTO_LIABILITY,
        premium: 15200,
        limit: 1_000_000,
        deductible: 2500,
      },
    ],
  },
]

/**
 * The two indications behind Blueline Freight Solutions' Dellwood offer, as production
 * served them: Rocklake rates each line of business on its own portal, so one carrier
 * answering on cargo and on physical damage arrives as two rows with two vendor ids.
 * Real payloads on purpose — the demo below runs them through the live mapper so the
 * grouped card is the mapper's own output, not a hand-built picture of one.
 */
const DELLWOOD_LINE_DEFAULTS = {
  dateUpdated: '2026-09-09T10:04:33Z',
  dateApplied: '',
  vendor: Vendor.ROCKLAKE,
  status: InsuranceIndicationStatus.GENERATED,
  vendorName: 'Dellwood',
  broker: null,
  carrierId: 'carrier-dellwood',
  // The dispatch token the Rocklake services stamp; without it the lines don't group.
  offerGroup: 'dispatch-975591',
  coverageLine: null,
  metadata: {},
  oneTimeFees: [],
  quotes: [],
  isCardVisible: true,
  portalLink: null,
  iconUrl: DELLWOOD_ICON_URL,
  iconAlt: 'Dellwood',
  perTruckLow: null,
  perTruckHigh: null,
  fleetLow: null,
  fleetHigh: null,
  carrierNote: null,
  brokerNote: '',
  brokerRecommended: false,
  brokerRecommendedByName: null,
  declineReason: null,
  declineReasonRaw: null,
  declineDetail: null,
  failureReason: null,
  isCurrentInsurer: false,
  isCustomerUploaded: false,
  carrierRating: 'A-',
  coiSpeed: CoiSpeed.UNDER_24_HOURS,
  carrierProgram: null,
  insuranceType: InsuranceType.NON_ADMITTED,
  carrierPublicNote: null,
  financingOptions: null,
  allowsIndividualCoveragePurchase: false,
  claimsCapabilities: null,
  sourceDocumentUrl: null,
  aiSummary: null,
  additionalInfoRequested: null,
  portalNoteUrl: null,
  needsAttestation: false,
  quoteDocument: null,
  summary: '',
  pros: '',
  cons: '',
  carrierBrand: 'Dellwood Specialty',
} satisfies Partial<InsuranceIndication>

export const DELLWOOD_LINES: InsuranceIndication[] = [
  {
    ...DELLWOOD_LINE_DEFAULTS,
    id: 'c402e49f-6627-465f-ba7c-6981c3e68223',
    dateCreated: '2026-08-31T20:28:16Z',
    dateSubmitted: '2026-08-31T20:28:16Z',
    pricedStartDate: null,
    vendorId: '975591',
    coverageLine: CoverageType.MOTOR_TRUCK_CARGO,
    premium: 1229.51,
    coverages: [
      {
        id: 'cov-mtc',
        coverageType: CoverageType.MOTOR_TRUCK_CARGO,
        vendorCoverageType: 'cargo_liability',
        premium: 1229.51,
        limit: 100_000,
        deductible: 1_000,
        premiumPerUnit: 0,
        label: 'Cargo',
        isPricePerTruck: false,
      },
    ],
  },
  {
    ...DELLWOOD_LINE_DEFAULTS,
    id: 'e7835de8-bf8a-4357-b6d7-929a04dd964a',
    dateCreated: '2026-08-31T20:27:59Z',
    dateSubmitted: '2026-08-31T20:27:59Z',
    pricedStartDate: null,
    vendorId: '975592',
    coverageLine: CoverageType.AUTO_PHYSICAL_DAMAGE,
    premium: 2314.47,
    coverages: [
      {
        id: 'cov-apd',
        coverageType: CoverageType.AUTO_PHYSICAL_DAMAGE,
        vendorCoverageType: 'physical_damage',
        premium: 2314.47,
        limit: 30_000,
        deductible: 2_500,
        premiumPerUnit: 0,
        label: 'Physical Damage',
        isPricePerTruck: false,
      },
    ],
  },
]

/** The same Dellwood dispatch with auto liability declined — one card, cargo still priced. */
export const DELLWOOD_PARTLY_DECLINED: InsuranceIndication[] = [
  DELLWOOD_LINES[0],
  {
    ...DELLWOOD_LINE_DEFAULTS,
    id: 'a1d0c6e8-3f4b-4c2a-9d1e-7b5f0a2c8e31',
    dateCreated: '2026-08-31T20:28:16Z',
    dateSubmitted: '2026-08-31T20:28:16Z',
    pricedStartDate: null,
    vendorId: '975593',
    status: InsuranceIndicationStatus.DECLINED,
    coverageLine: CoverageType.AUTO_LIABILITY,
    premium: 0,
    coverages: [],
  },
]

/** Neither line priced: one declined, one still with the carrier. Reads as one status line. */
export const DELLWOOD_DECLINED_AND_WAITING: InsuranceIndication[] = [
  DELLWOOD_PARTLY_DECLINED[1],
  {
    ...DELLWOOD_LINE_DEFAULTS,
    id: 'b2e1d7f9-4a5c-4d3b-8e2f-6c4a1b3d9f42',
    dateCreated: '2026-08-31T20:28:16Z',
    dateSubmitted: '2026-08-31T20:28:16Z',
    pricedStartDate: null,
    vendorId: '975594',
    status: InsuranceIndicationStatus.PENDING,
    coverageLine: CoverageType.MOTOR_TRUCK_CARGO,
    premium: 0,
    coverages: [],
  },
]
